import React from 'react';
import { Play, History } from 'lucide-react';

/**
 * Horizontal strip of the 3 most recently-played presets.
 * One tap re-loads the preset and bumps the user straight to Play.
 *
 * Props:
 *   history          - full session history array (latest first is assumed)
 *   presets          - full preset list; used to look up IDs by name
 *   onQuickStart(p)  - callback fired with the matched preset object
 */
const TIME_UNITS = [
  { sec: 60, label: 's' },
  { sec: 3600, label: 'm', div: 60 },
  { sec: 86400, label: 'h', div: 3600 },
  { sec: Infinity, label: 'd', div: 86400 },
];

const relativeTime = (iso) => {
  const diff = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  for (const u of TIME_UNITS) {
    if (diff < u.sec) return `${Math.floor(u.div ? diff / u.div : diff)}${u.label} ago`;
  }
  return 'a while ago';
};

const RecentSessionsStrip = ({ history = [], presets = [], onQuickStart }) => {
  if (!history.length || !presets.length) return null;

  // Dedupe by preset name, preserving most-recent-first order, keep 3
  const seen = new Set();
  const recent = [];
  for (const s of history) {
    if (!s?.preset || s.preset === 'Custom') continue;
    if (seen.has(s.preset)) continue;
    seen.add(s.preset);
    // Only include if the preset still exists in the catalog
    const match = presets.find((p) => p.name === s.preset);
    if (!match) continue;
    recent.push({ session: s, preset: match });
    if (recent.length >= 3) break;
  }
  if (!recent.length) return null;

  return (
    <div className="mb-3" data-testid="recent-sessions-strip">
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-2">
        <History className="w-3 h-3" /> Recently played
      </p>
      <div className="flex flex-wrap gap-2">
        {recent.map(({ session, preset }) => (
          <button
            key={session.id}
            type="button"
            onClick={() => onQuickStart(preset)}
            title={`Re-load ${preset.name}`}
            className="group inline-flex items-center gap-2 max-w-full bg-white/[0.04] hover:bg-gradient-to-r hover:from-purple-500/15 hover:to-cyan-500/15 border border-white/10 hover:border-purple-500/40 rounded-full pl-2.5 pr-3 py-1.5 text-xs transition-all"
            data-testid="recent-session-pill"
          >
            <span className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/30 flex items-center justify-center shrink-0 group-hover:from-purple-500 group-hover:to-cyan-500 transition">
              <Play className="w-2.5 h-2.5 text-white/80 group-hover:text-white fill-current" />
            </span>
            <span className="text-white/85 truncate max-w-[180px]">{preset.name}</span>
            <span className="text-white/35 text-[10px] shrink-0">· {relativeTime(session.date)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RecentSessionsStrip;
